import {
  NotificationsProvider,
  useApiClientConfig,
  useDarkModeClass,
  QAFloatingPanel,
} from '@breward/ui';
import { ApiClientProvider, useApiClient } from '@brewardapp/api-client';
import { useStore } from '@breward/store';
import formbricks from '@formbricks/js';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/pl';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import './App.css';
import Router from './router';

dayjs.locale('en');
dayjs.extend(localeData);
dayjs.extend(isBetween);
dayjs.extend(duration);

interface AppNotification {
  id?: string | number;
  type?: string;
  title?: string;
  message?: string;
  [key: string]: any;
}

const App = () => {
  const queryClient = useQueryClient();

  const userId = useStore((state) => state.userId);
  const notifications = useStore((state) => state.notifications) as AppNotification[] | undefined;
  const hideNotification = useStore((state) => state.hideNotification);
  useDarkModeClass();

  // if (typeof window !== 'undefined' && userId) {
  //   formbricks.init({
  //     environmentId: 'cm1s70zzq0008ej2ax5mpqksa',
  //     apiHost: 'https://xm.breward.app',
  //     userId: userId,
  //   });
  // }

  const handleNotificationClick = (id: string | number) => {
    if (hideNotification && typeof id === 'string') {
      hideNotification(id);
    }
  };

  const clientConfig = useApiClientConfig({
    apiUrl: import.meta.env.VITE_API_HOST + '/api/v1',
    scope: 'ParticipantWeb',
  });

  return (
    <div>
      <ApiClientProvider
        queryClient={queryClient}
        clientConfig={clientConfig}
      >
        <NotificationsProvider
          notifications={notifications || []}
          onNotificationClick={handleNotificationClick}
        >
          <Router />
          <QAFloatingPanelWrapper />
        </NotificationsProvider>
      </ApiClientProvider>
    </div>
  );
};

const QAFloatingPanelWrapper = () => {
  const authToken = useStore((state) => state.authToken);
  const api = useApiClient();
  const { data: currentUserData } = api.users.useCurrentUser({}, { enabled: !!authToken });
  const generateFlightMutation = api.qa.useGenerateMissingScoresheetsForFlight();
  const generateTableMutation = api.qa.useGenerateMissingScoresheetsForTable();
  const generateEntriesMutation = api.qa.useGenerateEntries();
  const payEntriesMutation = api.qa.usePayEntries();
  const checkInEntriesMutation = api.qa.useCheckInEntries();
  const recodeEntriesMutation = api.qa.useRecodeEntries();
  const createFlightsMutation = api.qa.useCreateFlights();
  const assignTablesMutation = api.qa.useAssignTables();

  return (
    <QAFloatingPanel
      currentUser={currentUserData?.data}
      onGenerateMissingScoresheetsForFlight={async (payload) => {
        return await generateFlightMutation.mutateAsync(payload);
      }}
      onGenerateMissingScoresheetsForTable={async (payload) => {
        return await generateTableMutation.mutateAsync(payload);
      }}
      onGenerateEntries={async (payload) => {
        return await generateEntriesMutation.mutateAsync(payload);
      }}
      onPayEntries={async (payload) => {
        return await payEntriesMutation.mutateAsync(payload);
      }}
      onCheckInEntries={async (payload) => {
        return await checkInEntriesMutation.mutateAsync(payload);
      }}
      onRecodeEntries={async (payload) => {
        return await recodeEntriesMutation.mutateAsync(payload);
      }}
      onCreateFlights={async (payload) => {
        return await createFlightsMutation.mutateAsync(payload);
      }}
      onAssignTables={async (payload) => {
        return await assignTablesMutation.mutateAsync(payload);
      }}
    />
  );
};

export default App;
